import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Hotel as HotelIcon,
  Link as LinkIcon,
  MeetingRoom as MeetingRoomIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Sync as SyncIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  getMappingDataByHotelId,
  triggerRateSync,
  updateMappingStayflexiId,
  deleteMappingRow,
  mapTabtripsWithStayflexi,
  mapRoomTypeAndRatePlan,
  updateRoomTypeField,
  deleteRoomTypeAdmin,
  fetchStayflexiRoomTypeMappings,
  getAvailableRoomTypes,
  changeRoomType,
  setAmenities,
} from '../api/adminPanelApi';

const HIGHLIGHT_MS = 3000;

const DEFAULT_HOTEL_AMENITIES = [
  'Air Conditioning', 'Laundry', 'Newspaper', 'Room Service', 'Swimming Pool', 'Wifi', 'Reception',
  'Restaurant', 'Luggage Assistance', 'CCTV', 'First-aid Services', 'Basic Facilities',
  'Kitchen/Kitchenette', 'Power Backup', 'Refrigerator', 'Washing Machine', 'General Services',
  'Multilingual Staff', 'Wake-up Call/Service', 'Bonfire', 'Common Areas', 'Balcony/Terrace',
  'Indoor Seating Area', 'Parking', 'Lounge', 'Wheelchair Accessibility', 'Fire Extinguishers',
  'Airport Transfers', 'Housekeeping', "Driver's Accommodation", 'Luggage Storage',
  'Specially Abled Assistance', 'Doctor on Call', 'Pool/Beach Towels', 'Outdoor Activities and Sports',
  'Outdoor Sports (Cricket, Volleyball, Basketball, Tennis, Badminton)', 'Water Sports', 'Water Park',
  'Canoeing', 'Diving', 'Cycling', 'Lawn', 'Prayer Room', 'Living Room', 'Outdoor Furniture',
  'Game Room', 'Sitout Area', 'Food and Drink', 'Dining Area', 'Business Center and Conferences',
  'Business Center', 'Conference Room',
];

const DEFAULT_ROOM_AMENITIES = [
  'City', 'Shower', 'Toiletries', 'Mirror', 'Walk-in shower', 'Shower chair', 'Cleaning products',
  'Towels', 'Private bathroom', 'On-demand movies', 'Internet access – wireless', 'Internet access – LAN',
  'TV', 'TV [flat screen]', 'Satellite/cable channels', 'Wi-Fi [free]', 'Telephone', 'In-room tablet',
  'Streaming service like Netflix', 'Reading light', 'Free use of business areas', 'Activity Kits', 'Desk',
  'Balcony/terrace', 'Private pool', 'Seating area', 'Extra long bed', 'Interconnecting room(s) available',
  'High chair', 'Sofa', 'Wooden/parqueted flooring', 'Separate living room', 'Laptop workspace', 'High floor',
  'Top floor available', 'Ground floor available', 'Low floor available', 'Tile/marble flooring',
  'Outdoor furniture', 'Window', 'Separate dining area', 'Window that opens', 'Openable window', 'Locker',
  'Smoking allowed', 'Fan', 'Wake-up service', 'Slippers', 'Sleep comfort items', 'Socket near the bed',
  'Free laundry service',
];

function parseAmenitiesString(raw) {
  if (Array.isArray(raw)) return raw.map((a) => String(a).trim()).filter(Boolean);
  if (!raw || typeof raw !== 'string') return [];
  return raw.split(',').map((a) => a.trim()).filter(Boolean);
}

function amenitiesToPayloadString(list) {
  return list.map((a) => a.trim()).filter(Boolean).join(', ');
}

const MappingData = () => {
  const navigate = useNavigate();
  const [hotelId, setHotelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [data, setData] = useState(null);
  const [highlightCell, setHighlightCell] = useState(null); // { rowKey, field } -> clear after HIGHLIGHT_MS

  const currentHotelId = useMemo(() => (data?.hotel?.hotel_id != null ? String(data.hotel.hotel_id) : ''), [data]);

  const refreshData = useCallback(async () => {
    if (!currentHotelId) return;
    setLoading(true);
    try {
      const res = await getMappingDataByHotelId(currentHotelId);
      if (res.success) {
        setData({
          hotel: res.hotel,
          mapping: res.mapping || [],
          room_types: res.room_types || [],
        });
      }
    } finally {
      setLoading(false);
    }
  }, [currentHotelId]);

  const handleFetch = async () => {
    const id = (hotelId || '').toString().trim();
    if (!id) {
      setSnackbar({ open: true, message: 'Please enter a Hotel ID', severity: 'warning' });
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await getMappingDataByHotelId(id);
      if (res.success) {
        setData({
          hotel: res.hotel,
          mapping: res.mapping || [],
          room_types: res.room_types || [],
        });
        setSnackbar({ open: true, message: res.message || `Mapping data loaded for hotel ${id}`, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: res.error || 'Failed to load mapping data', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to load', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const showSnack = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // ---- Mapping: Edit StayFlexi ID ----
  const [editMappingRow, setEditMappingRow] = useState(null);
  const [editMappingSfId, setEditMappingSfId] = useState('');
  const [mappingActionLoading, setMappingActionLoading] = useState(false);

  const openEditMapping = (row) => {
    setEditMappingRow(row);
    setEditMappingSfId(String(row.stayflexi_hotel_id ?? ''));
  };

  const saveEditMapping = async () => {
    if (!editMappingRow || editMappingSfId.trim() === '') return;
    setMappingActionLoading(true);
    try {
      const res = await updateMappingStayflexiId(
        editMappingRow.tabtrips_hotel_id,
        editMappingRow.stayflexi_hotel_id,
        editMappingRow.use_stayflexi,
        editMappingSfId.trim()
      );
      if (res.success) {
        showSnack(res.message || 'Mapping updated');
        setEditMappingRow(null);
        refreshData();
      } else {
        showSnack(res.error || 'Update failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Update failed', 'error');
    } finally {
      setMappingActionLoading(false);
    }
  };

  // ---- Mapping: Delete ----
  const [deleteMappingRowState, setDeleteMappingRowState] = useState(null);

  const confirmDeleteMapping = async () => {
    if (!deleteMappingRowState) return;
    setMappingActionLoading(true);
    try {
      const res = await deleteMappingRow(
        deleteMappingRowState.tabtrips_hotel_id,
        deleteMappingRowState.stayflexi_hotel_id,
        deleteMappingRowState.use_stayflexi
      );
      if (res.success) {
        showSnack(res.message || 'Mapping deleted');
        setDeleteMappingRowState(null);
        refreshData();
      } else {
        showSnack(res.error || 'Delete failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Delete failed', 'error');
    } finally {
      setMappingActionLoading(false);
    }
  };

  // ---- Mapping: Add ----
  const [addMappingOpen, setAddMappingOpen] = useState(false);
  const [addSfId, setAddSfId] = useState('');
  const [addUseStayflexi, setAddUseStayflexi] = useState('yes');

  const hasYes = useMemo(() => (data?.mapping || []).some((r) => (r.use_stayflexi || '').toLowerCase() === 'yes'), [data?.mapping]);
  const hasNo = useMemo(() => (data?.mapping || []).some((r) => (r.use_stayflexi || '').toLowerCase() === 'no'), [data?.mapping]);

  const submitAddMapping = async () => {
    const id = currentHotelId;
    if (!id || !addSfId.trim()) {
      showSnack('StayFlexi Hotel ID is required', 'warning');
      return;
    }
    if (addUseStayflexi === 'yes' && hasYes) {
      showSnack('A mapping with Use StayFlexi "yes" already exists for this hotel.', 'error');
      return;
    }
    if (addUseStayflexi === 'no' && hasNo) {
      showSnack('A mapping with Use StayFlexi "no" already exists for this hotel.', 'error');
      return;
    }
    setMappingActionLoading(true);
    try {
      const res = await mapTabtripsWithStayflexi(id, addSfId.trim(), addUseStayflexi);
      if (res.success) {
        showSnack(res.data?.message || 'Mapping added');
        setAddMappingOpen(false);
        setAddSfId('');
        setAddUseStayflexi('yes');
        refreshData();
      } else {
        showSnack(res.error || 'Add failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Add failed', 'error');
    } finally {
      setMappingActionLoading(false);
    }
  };

  // ---- Room type: Sync ----
  const [syncLoading, setSyncLoading] = useState(false);
  const [rateSyncLoading, setRateSyncLoading] = useState(false);
  const handleSync = async () => {
    if (!currentHotelId) return;
    setSyncLoading(true);
    try {
      const res = await fetchStayflexiRoomTypeMappings(currentHotelId);
      if (res.success) {
        showSnack('Sync completed');
        refreshData();
      } else {
        showSnack(res.error || 'Sync failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Sync failed', 'error');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleRateSync = async () => {
    const targetHotelId = (hotelId || currentHotelId || '').toString().trim();
    if (!targetHotelId) {
      showSnack('Please enter a Hotel ID', 'warning');
      return;
    }
    setRateSyncLoading(true);
    try {
      const res = await triggerRateSync(targetHotelId);
      if (res.success) {
        showSnack(res.message || `Rate sync triggered for hotel ${targetHotelId}`);
      } else {
        showSnack(res.error || res.message || 'Rate sync failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Rate sync failed', 'error');
    } finally {
      setRateSyncLoading(false);
    }
  };

  // ---- Room type: Update field (single) + highlight ----
  const triggerHighlight = (rowKey, field) => {
    setHighlightCell({ rowKey, field });
    setTimeout(() => setHighlightCell((c) => (c?.rowKey === rowKey && c?.field === field ? null : c)), HIGHLIGHT_MS);
  };

  const [roomTypeUpdating, setRoomTypeUpdating] = useState(null); // { roomtype_id, field }

  const updateRoomField = async (row, field, value) => {
    const key = `${row.roomtype_id}`;
    setRoomTypeUpdating({ roomtype_id: row.roomtype_id, field });
    try {
      let res;
      if (field === 'total_room_count' || field === 'occupancy' || field === 'isActive') {
        res = await updateRoomTypeField(currentHotelId, row.roomtype_id, field, field === 'isActive' ? (value ? 1 : 0) : Number(value));
      } else if (field === 'sf_rateplan_id') {
        res = await mapRoomTypeAndRatePlan(currentHotelId, row.roomtype_id, {
          stayflexiRatePlanId: Number(value),
          stayflexiRoomTypeId: row.sf_roomtype_id != null ? Number(row.sf_roomtype_id) : undefined,
        });
      } else if (field === 'sf_roomtype_id') {
        res = await mapRoomTypeAndRatePlan(currentHotelId, row.roomtype_id, {
          stayflexiRoomTypeId: Number(value),
          stayflexiRatePlanId: row.sf_rateplan_id != null ? Number(row.sf_rateplan_id) : undefined,
        });
      } else {
        setRoomTypeUpdating(null);
        return;
      }
      if (res.success) {
        triggerHighlight(key, field);
        refreshData();
      } else {
        showSnack(res.error || 'Update failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Update failed', 'error');
    } finally {
      setRoomTypeUpdating(null);
    }
  };

  // ---- Room type: Change name dialog ----
  const normalizeForSearch = (s) => (s || '').toLowerCase().replace(/[\s_]/g, '');
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [nameDialogRow, setNameDialogRow] = useState(null);
  const [roomTypeSearch, setRoomTypeSearch] = useState('');
  const [roomTypeSuggestions, setRoomTypeSuggestions] = useState([]);
  const [roomTypeSuggestionsLoading, setRoomTypeSuggestionsLoading] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [changeNameLoading, setChangeNameLoading] = useState(false);

  const loadSuggestions = useCallback(async () => {
    setRoomTypeSuggestionsLoading(true);
    try {
      const res = await getAvailableRoomTypes();
      if (res.success && Array.isArray(res.data)) {
        setRoomTypeSuggestions(res.data);
      } else {
        setRoomTypeSuggestions([]);
      }
    } finally {
      setRoomTypeSuggestionsLoading(false);
    }
  }, []);

  const openNameDialog = (row) => {
    setNameDialogRow(row);
    setRoomTypeSearch('');
    setSelectedCategoryId(null);
    setNameDialogOpen(true);
    loadSuggestions();
  };

  const filteredSuggestions = useMemo(() => {
    const q = normalizeForSearch(roomTypeSearch);
    if (!q) return roomTypeSuggestions.slice(0, 20);
    return roomTypeSuggestions.filter((item) => {
      const name = normalizeForSearch(item.category_name || item.safe_name || '');
      return name.includes(q) || name.indexOf(q) === 0;
    }).slice(0, 20);
  }, [roomTypeSearch, roomTypeSuggestions]);

  const handleChangeRoomTypeName = async () => {
    if (!nameDialogRow || selectedCategoryId == null || !currentHotelId) return;
    setChangeNameLoading(true);
    try {
      const res = await changeRoomType(currentHotelId, nameDialogRow.roomtype_id, selectedCategoryId);
      if (res.success) {
        showSnack(res.message || 'Room type name updated');
        setNameDialogOpen(false);
        refreshData();
      } else {
        showSnack(res.error || 'Failed to change room type', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Failed', 'error');
    } finally {
      setChangeNameLoading(false);
    }
  };

  // ---- Room type: Delete ----
  const [deleteRoomTypeState, setDeleteRoomTypeState] = useState(null);
  const [deleteRoomTypeLoading, setDeleteRoomTypeLoading] = useState(false);

  const confirmDeleteRoomType = async () => {
    if (!deleteRoomTypeState || !currentHotelId) return;
    setDeleteRoomTypeLoading(true);
    try {
      const res = await deleteRoomTypeAdmin(currentHotelId, deleteRoomTypeState.roomtype_id);
      if (res.success) {
        showSnack('Room type deleted');
        setDeleteRoomTypeState(null);
        refreshData();
      } else {
        showSnack(res.error || 'Delete failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Delete failed', 'error');
    } finally {
      setDeleteRoomTypeLoading(false);
    }
  };

  // ---- Amenities ----
  const [hotelAmenitiesOpen, setHotelAmenitiesOpen] = useState(false);
  const [roomAmenitiesOpen, setRoomAmenitiesOpen] = useState(false);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [amenitiesCustomInput, setAmenitiesCustomInput] = useState('');
  const [amenitiesSaving, setAmenitiesSaving] = useState(false);
  const [roomAmenitiesRoomtypeIds, setRoomAmenitiesRoomtypeIds] = useState([]);

  const roomTypesForAmenities = useMemo(() => data?.room_types || [], [data?.room_types]);

  const openHotelAmenities = () => {
    const existing = parseAmenitiesString(data?.hotel?.amenities);
    setAmenitiesList(existing.length > 0 ? [...existing] : [...DEFAULT_HOTEL_AMENITIES]);
    setAmenitiesCustomInput('');
    setHotelAmenitiesOpen(true);
  };

  const openRoomAmenities = () => {
    setRoomAmenitiesRoomtypeIds([]);
    setAmenitiesList([]);
    setAmenitiesCustomInput('');
    setRoomAmenitiesOpen(true);
  };

  const loadAmenitiesForRoomTypeSelection = (ids, previousIds = []) => {
    if (ids.length === 0) {
      setAmenitiesList([]);
      return;
    }
    if (ids.length === 1) {
      const onlyId = Number(ids[0]);
      const prevOnlyId = previousIds.length === 1 ? Number(previousIds[0]) : null;
      if (prevOnlyId === onlyId) return;
      const row = roomTypesForAmenities.find((r) => Number(r.roomtype_id) === onlyId);
      const existing = parseAmenitiesString(row?.amenities);
      setAmenitiesList(existing.length > 0 ? [...existing] : [...DEFAULT_ROOM_AMENITIES]);
      return;
    }
    if (previousIds.length === 0) {
      setAmenitiesList([...DEFAULT_ROOM_AMENITIES]);
    }
  };

  const onRoomTypesSelectionChange = (ids) => {
    const normalized = ids.map((id) => Number(id));
    setRoomAmenitiesRoomtypeIds((prev) => {
      loadAmenitiesForRoomTypeSelection(normalized, prev);
      return normalized;
    });
    setAmenitiesCustomInput('');
  };

  const toggleSelectAllRoomTypes = () => {
    const allIds = roomTypesForAmenities.map((rt) => Number(rt.roomtype_id));
    if (roomAmenitiesRoomtypeIds.length === allIds.length) {
      onRoomTypesSelectionChange([]);
    } else {
      onRoomTypesSelectionChange(allIds);
    }
  };

  const removeAmenityItem = (label) => {
    setAmenitiesList((prev) => prev.filter((a) => a !== label));
  };

  const addCustomAmenity = () => {
    const label = amenitiesCustomInput.trim();
    if (!label) return;
    setAmenitiesList((prev) => (prev.includes(label) ? prev : [...prev, label]));
    setAmenitiesCustomInput('');
  };

  const saveHotelAmenities = async () => {
    if (!currentHotelId) return;
    setAmenitiesSaving(true);
    try {
      const res = await setAmenities({
        hotel_id: Number(currentHotelId),
        type: 'hotel',
        amenities: amenitiesToPayloadString(amenitiesList),
      });
      if (res.success) {
        showSnack(res.message || 'Hotel amenities saved');
        setHotelAmenitiesOpen(false);
        refreshData();
      } else {
        showSnack(res.error || 'Failed to save amenities', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Failed to save amenities', 'error');
    } finally {
      setAmenitiesSaving(false);
    }
  };

  const saveRoomAmenities = async () => {
    if (!currentHotelId || roomAmenitiesRoomtypeIds.length === 0) {
      showSnack('Please select at least one room type', 'warning');
      return;
    }
    setAmenitiesSaving(true);
    try {
      const res = await setAmenities({
        hotel_id: Number(currentHotelId),
        type: 'roomtype',
        roomtype_ids: roomAmenitiesRoomtypeIds.map((id) => Number(id)),
        amenities: amenitiesList.map((a) => a.trim()).filter(Boolean),
      });
      if (res.success) {
        const count = roomAmenitiesRoomtypeIds.length;
        showSnack(res.message || `Room amenities saved for ${count} room type${count === 1 ? '' : 's'}`);
        setRoomAmenitiesOpen(false);
        refreshData();
      } else {
        showSnack(res.error || 'Failed to save amenities', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Failed to save amenities', 'error');
    } finally {
      setAmenitiesSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      <Box sx={{ px: 2, py: 2, background: 'linear-gradient(135deg, #2E4057 0%, #1A252F 100%)', color: 'white', boxShadow: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <IconButton onClick={() => navigate('/')} sx={{ color: 'white' }} size="large">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Mapping Data</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>View and edit hotel mapping and room types</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, pt: 3 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 2 }}>Enter Hotel ID</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Hotel ID"
              value={hotelId}
              onChange={(e) => setHotelId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleFetch()}
              placeholder="e.g. 50050001"
              size="medium"
              sx={{ minWidth: 220 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            />
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              sx={{ bgcolor: 'primary.main', px: 3, '&:hover': { bgcolor: 'primary.dark' } }}
            >
              {loading ? 'Loading…' : 'Get Mapping Data'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleRateSync}
              disabled={rateSyncLoading}
              startIcon={rateSyncLoading ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {rateSyncLoading ? 'Syncing…' : 'Rate Sync'}
            </Button>
          </Box>
        </Paper>

        {data && (
          <>
            {data.hotel && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                  <HotelIcon sx={{ fontSize: 32, opacity: 0.9, flexShrink: 0 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Hotel ID: {data.hotel.hotel_id}</Typography>
                    <Typography variant="h6" fontWeight={700} noWrap title={data.hotel.hotel_name || undefined}>
                      {data.hotel.hotel_name || '–'}
                    </Typography>
                    {data.hotel.amenities && (
                      <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', mt: 0.5 }}>
                        Current: {parseAmenitiesString(data.hotel.amenities).length} amenit{parseAmenitiesString(data.hotel.amenities).length === 1 ? 'y' : 'ies'}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={openHotelAmenities}
                  sx={{
                    color: 'white',
                    borderColor: 'rgba(255,255,255,0.7)',
                    flexShrink: 0,
                    '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Add Amenities
                </Button>
              </Paper>
            )}

            {/* Mapping table */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#2E4057', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon />
                  <Typography variant="subtitle1" fontWeight={700}>Mapping</Typography>
                  <Chip label={`${(data.mapping || []).length} row(s)`} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => { setAddMappingOpen(true); setAddSfId(''); setAddUseStayflexi(hasYes ? 'no' : 'yes'); }}
                  sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  Add mapping data
                </Button>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>StayFlexi Hotel ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>TabTrips Hotel ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Use StayFlexi</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!data.mapping || data.mapping.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 4 }}>No mapping data. Use “Add mapping data” to add a row.</TableCell>
                      </TableRow>
                    ) : (
                      data.mapping.map((row, idx) => (
                        <TableRow key={`${row.tabtrips_hotel_id}-${row.stayflexi_hotel_id}-${row.use_stayflexi}-${idx}`} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{row.stayflexi_hotel_id ?? '–'}</TableCell>
                          <TableCell>{row.tabtrips_hotel_id ?? '–'}</TableCell>
                          <TableCell>
                            <Chip
                              label={row.use_stayflexi ?? '–'}
                              size="small"
                              color={(row.use_stayflexi || '').toLowerCase() === 'yes' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditMapping(row)} title="Edit StayFlexi Hotel ID"><EditIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => setDeleteMappingRowState(row)} title="Delete"><DeleteIcon fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Room types table */}
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#2E4057', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MeetingRoomIcon />
                  <Typography variant="subtitle1" fontWeight={700}>Room Types</Typography>
                  <Chip label={`${(data.room_types || []).length} row(s)`} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openRoomAmenities}
                    disabled={!data.room_types?.length}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    Add Room Amenities
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={syncLoading ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    disabled={syncLoading}
                    onClick={handleSync}
                    sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                  >
                    Sync mapping data
                  </Button>
                </Box>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Room Type ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Room Type Name</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Total Rooms</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Occupancy</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>SF Rate Plan ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>SF Room Type ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">Delete</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!data.room_types || data.room_types.length === 0) ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>No room types</TableCell>
                      </TableRow>
                    ) : (
                      data.room_types.map((row) => {
                        const rowKey = `${row.roomtype_id}`;
                        const isUpdating = roomTypeUpdating?.roomtype_id === row.roomtype_id && roomTypeUpdating?.field;
                        const hl = highlightCell?.rowKey === rowKey && highlightCell?.field;
                        const cellSx = (field) => (hl === field ? { bgcolor: 'rgba(129,199,132,0.35)', transition: 'background 0.2s' } : {});
                        return (
                          <TableRow key={rowKey} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{row.roomtype_id ?? '–'}</TableCell>
                            <TableCell>
                              <Typography
                                component="button"
                                onClick={() => openNameDialog(row)}
                                sx={{ cursor: 'pointer', textAlign: 'left', border: 'none', background: 'none', textDecoration: 'underline', color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                              >
                                {row.roomtype_name ?? '–'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={cellSx('total_room_count')}>
                              <EditableCell
                                value={row.total_room_count}
                                type="number"
                                saving={isUpdating === 'total_room_count'}
                                onSave={(v) => updateRoomField(row, 'total_room_count', v)}
                              />
                            </TableCell>
                            <TableCell align="right" sx={cellSx('occupancy')}>
                              <EditableCell
                                value={row.occupancy}
                                type="number"
                                saving={isUpdating === 'occupancy'}
                                onSave={(v) => updateRoomField(row, 'occupancy', v)}
                              />
                            </TableCell>
                            <TableCell sx={cellSx('sf_rateplan_id')}>
                              <EditableCell
                                value={row.sf_rateplan_id}
                                type="number"
                                saving={isUpdating === 'sf_rateplan_id'}
                                onSave={(v) => updateRoomField(row, 'sf_rateplan_id', v)}
                              />
                            </TableCell>
                            <TableCell sx={cellSx('sf_roomtype_id')}>
                              <EditableCell
                                value={row.sf_roomtype_id}
                                type="number"
                                saving={isUpdating === 'sf_roomtype_id'}
                                onSave={(v) => updateRoomField(row, 'sf_roomtype_id', v)}
                              />
                            </TableCell>
                            <TableCell sx={cellSx('isActive')}>
                              <ActiveToggle value={row.isActive} saving={isUpdating === 'isActive'} onSave={(v) => updateRoomField(row, 'isActive', v)} />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="error" onClick={() => setDeleteRoomTypeState(row)} title="Delete room type"><DeleteIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Box>

      {/* Edit mapping StayFlexi ID dialog */}
      <Dialog open={!!editMappingRow} onClose={() => !mappingActionLoading && setEditMappingRow(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit StayFlexi Hotel ID</DialogTitle>
        <DialogContent>
          {editMappingRow && (
            <TextField
              fullWidth
              label="New StayFlexi Hotel ID"
              value={editMappingSfId}
              onChange={(e) => setEditMappingSfId(e.target.value)}
              sx={{ mt: 1 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditMappingRow(null)} disabled={mappingActionLoading}>Cancel</Button>
          <Button variant="contained" onClick={saveEditMapping} disabled={mappingActionLoading || !editMappingSfId.trim()}>
            {mappingActionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete mapping confirm */}
      <Dialog open={!!deleteMappingRowState} onClose={() => !mappingActionLoading && setDeleteMappingRowState(null)}>
        <DialogTitle>Delete mapping</DialogTitle>
        <DialogContent>Are you sure you want to delete the mapping?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMappingRowState(null)} disabled={mappingActionLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteMapping} disabled={mappingActionLoading}>
            {mappingActionLoading ? <CircularProgress size={20} /> : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add mapping dialog */}
      <Dialog open={addMappingOpen} onClose={() => !mappingActionLoading && setAddMappingOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add mapping data</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="TabTrips Hotel ID" value={currentHotelId} disabled sx={{ mt: 1 }} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth label="StayFlexi Hotel ID" value={addSfId} onChange={(e) => setAddSfId(e.target.value)} required sx={{ mt: 2 }} InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth sx={{ mt: 2 }} size="medium">
            <InputLabel shrink>Use StayFlexi</InputLabel>
            <Select value={addUseStayflexi} onChange={(e) => setAddUseStayflexi(e.target.value)} label="Use StayFlexi">
              <MenuItem value="yes" disabled={hasYes}>Yes</MenuItem>
              <MenuItem value="no" disabled={hasNo}>No</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMappingOpen(false)} disabled={mappingActionLoading}>Cancel</Button>
          <Button variant="contained" onClick={submitAddMapping} disabled={mappingActionLoading || !addSfId.trim()}>
            {mappingActionLoading ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change room type name dialog */}
      <Dialog open={nameDialogOpen} onClose={() => !changeNameLoading && setNameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change roomtype name</DialogTitle>
        <DialogContent>
          {nameDialogRow && (
            <>
              <Typography variant="body2" color="text.secondary">Room Type ID: {nameDialogRow.roomtype_id}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Current roomtype name: {nameDialogRow.roomtype_name}</Typography>
              <TextField
                fullWidth
                placeholder="Search room type (e.g. deluxe)"
                value={roomTypeSearch}
                onChange={(e) => { setRoomTypeSearch(e.target.value); setSelectedCategoryId(null); }}
                InputLabelProps={{ shrink: true }}
                InputProps={{ endAdornment: roomTypeSuggestionsLoading ? <InputAdornment position="end"><CircularProgress size={20} /></InputAdornment> : null }}
              />
              <Box sx={{ maxHeight: 240, overflow: 'auto', mt: 1 }}>
                {filteredSuggestions.map((item) => {
                  const id = item.id ?? item.roomtype_id;
                  const name = item.category_name ?? item.safe_name ?? item.roomtype_name ?? '–';
                  const selected = selectedCategoryId === id;
                  return (
                    <Box
                      key={id}
                      onClick={() => setSelectedCategoryId(id)}
                      sx={{
                        p: 1,
                        cursor: 'pointer',
                        bgcolor: selected ? 'primary.light' : 'transparent',
                        color: selected ? 'primary.contrastText' : 'text.primary',
                        borderRadius: 1,
                        '&:hover': { bgcolor: selected ? 'primary.main' : 'action.hover' },
                      }}
                    >
                      {name} {item.safe_name && item.safe_name !== name ? `(${item.safe_name})` : ''}
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)} disabled={changeNameLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleChangeRoomTypeName} disabled={changeNameLoading || selectedCategoryId == null}>
            {changeNameLoading ? <CircularProgress size={20} /> : 'Change roomtype'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hotel amenities */}
      <AmenitiesEditorDialog
        open={hotelAmenitiesOpen}
        title="Set hotel amenities"
        subtitle={data?.hotel ? `${data.hotel.hotel_name || 'Hotel'} (ID: ${data.hotel.hotel_id})` : ''}
        items={amenitiesList}
        customInput={amenitiesCustomInput}
        onCustomInputChange={setAmenitiesCustomInput}
        onAddCustom={addCustomAmenity}
        onRemove={removeAmenityItem}
        saving={amenitiesSaving}
        onClose={() => !amenitiesSaving && setHotelAmenitiesOpen(false)}
        onSave={saveHotelAmenities}
      />

      {/* Room type amenities */}
      <Dialog
        open={roomAmenitiesOpen}
        onClose={() => !amenitiesSaving && setRoomAmenitiesOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add room amenities</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Room types {roomAmenitiesRoomtypeIds.length > 0 ? `(${roomAmenitiesRoomtypeIds.length} selected)` : ''}
            </Typography>
            <Button
              size="small"
              variant="text"
              onClick={toggleSelectAllRoomTypes}
              disabled={roomTypesForAmenities.length === 0}
            >
              {roomAmenitiesRoomtypeIds.length === roomTypesForAmenities.length && roomTypesForAmenities.length > 0
                ? 'Clear all'
                : 'Select all'}
            </Button>
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel shrink>Room types</InputLabel>
            <Select
              multiple
              value={roomAmenitiesRoomtypeIds}
              label="Room types"
              onChange={(e) => {
                const value = e.target.value;
                onRoomTypesSelectionChange(typeof value === 'string' ? value.split(',').map(Number) : value);
              }}
              renderValue={(selected) => {
                if (!selected.length) return 'Select room type(s)';
                if (selected.length === roomTypesForAmenities.length) return 'All room types';
                return selected
                  .map((id) => {
                    const rt = roomTypesForAmenities.find((r) => Number(r.roomtype_id) === Number(id));
                    return rt?.roomtype_name ? `${rt.roomtype_name} (${id})` : String(id);
                  })
                  .join(', ');
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              {roomTypesForAmenities.map((rt) => {
                const id = Number(rt.roomtype_id);
                const checked = roomAmenitiesRoomtypeIds.includes(id);
                return (
                  <MenuItem key={rt.roomtype_id} value={id}>
                    <Checkbox size="small" checked={checked} sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText
                      primary={rt.roomtype_name ?? '–'}
                      secondary={`ID: ${rt.roomtype_id}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {roomAmenitiesRoomtypeIds.length > 0 ? (
            <AmenitiesListEditor
              items={amenitiesList}
              customInput={amenitiesCustomInput}
              onCustomInputChange={setAmenitiesCustomInput}
              onAddCustom={addCustomAmenity}
              onRemove={removeAmenityItem}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select one or more room types to load prefilled amenities.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomAmenitiesOpen(false)} disabled={amenitiesSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveRoomAmenities}
            disabled={amenitiesSaving || roomAmenitiesRoomtypeIds.length === 0}
          >
            {amenitiesSaving ? <CircularProgress size={20} /> : 'Save amenities'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete room type confirm */}
      <Dialog open={!!deleteRoomTypeState} onClose={() => !deleteRoomTypeLoading && setDeleteRoomTypeState(null)}>
        <DialogTitle>Delete room type</DialogTitle>
        <DialogContent>Are you sure you want to delete this room type?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRoomTypeState(null)} disabled={deleteRoomTypeLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteRoomType} disabled={deleteRoomTypeLoading}>
            {deleteRoomTypeLoading ? <CircularProgress size={20} /> : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

function AmenitiesListEditor({ items, customInput, onCustomInputChange, onAddCustom, onRemove }) {
  const [filter, setFilter] = useState('');

  const filteredItems = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((label) => label.toLowerCase().includes(q));
  }, [items, filter]);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Chip
          size="small"
          label={`${items.length} selected`}
          color={items.length > 0 ? 'primary' : 'default'}
          variant="outlined"
        />
        {items.length > 0 && filter && (
          <Typography variant="caption" color="text.secondary">
            Showing {filteredItems.length} of {items.length}
          </Typography>
        )}
      </Box>
      <TextField
        fullWidth
        size="small"
        placeholder="Search amenities…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: filter ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setFilter('')} aria-label="Clear search">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{ mb: 1.5 }}
      />
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          maxHeight: 320,
          overflow: 'auto',
          bgcolor: 'grey.50',
        }}
      >
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No amenities selected. Add a custom one below.
          </Typography>
        ) : filteredItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No amenities match your search.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {filteredItems.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                onDelete={() => onRemove(label)}
                sx={{
                  maxWidth: '100%',
                  height: 'auto',
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    py: 0.75,
                    lineHeight: 1.35,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Paper>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Add custom amenity
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. Pet friendly"
          value={customInput}
          onChange={(e) => onCustomInputChange(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onAddCustom()}
        />
        <Button variant="outlined" onClick={onAddCustom} disabled={!customInput.trim()} sx={{ flexShrink: 0, minWidth: 72 }}>
          Add
        </Button>
      </Box>
    </>
  );
}

function AmenitiesEditorDialog({
  open,
  title,
  subtitle,
  items,
  customInput,
  onCustomInputChange,
  onAddCustom,
  onRemove,
  saving,
  onClose,
  onSave,
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        <AmenitiesListEditor
          items={items}
          customInput={customInput}
          onCustomInputChange={onCustomInputChange}
          onAddCustom={onAddCustom}
          onRemove={onRemove}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save amenities'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditableCell({ value, type, saving, onSave }) {
  const [edit, setEdit] = useState(false);
  const [local, setLocal] = useState(value != null ? String(value) : '');

  const handleBlur = () => {
    if (type === 'number') {
      const n = local.trim() === '' ? null : Number(local);
      if (n !== value && (n === null || !Number.isNaN(n))) onSave(n);
    } else {
      if (local !== value) onSave(local);
    }
    setEdit(false);
  };

  if (edit) {
    return (
      <TextField
        size="small"
        type={type}
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => e.key === 'Enter' && handleBlur()}
        autoFocus
        sx={{
          width: 72,
          ...(type === 'number' && {
            '& input': { MozAppearance: 'textfield' },
            '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          }),
        }}
        inputProps={{ min: type === 'number' ? 0 : undefined }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => { setEdit(true); setLocal(value != null ? String(value) : ''); }}
      sx={{ cursor: 'pointer', display: 'inline-block', minWidth: 32 }}
    >
      {value != null && value !== '' ? String(value) : '–'}
      {saving && <CircularProgress size={14} sx={{ ml: 0.5, verticalAlign: 'middle' }} />}
    </Box>
  );
}

function ActiveToggle({ value, saving, onSave }) {
  const isActive = value === 1;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={isActive ? 'Yes' : 'No'}
        size="small"
        color={isActive ? 'success' : 'default'}
        variant="outlined"
        onClick={() => onSave(!isActive)}
        sx={{ cursor: 'pointer' }}
      />
      {saving && <CircularProgress size={14} />}
    </Box>
  );
}

export default MappingData;
